import React, { useState } from 'react';
import {
    Table,
    Typography,
    Button,
    Space,
    Input,
    InputNumber,
    DatePicker,
    Tag,
    Modal,
    Toast
} from '@douyinfe/semi-ui';
import {
    IconPlus,
    IconDelete,
    IconSave,
    IconEdit,
    IconBranch
} from '@douyinfe/semi-icons';
import { formatAmountZhCn,formatAmountZhCnZero1, formatDate,   } from "../../../utils";


const { Text } = Typography;

interface BankLendingTableProps {
    data: any[];
    onChange: (data: any[]) => void;
    loading?: boolean;
}

const BankLendingTable: React.FC<BankLendingTableProps> = ({
    data,
    onChange,
    loading = false
}) => {
    const [editingKey, setEditingKey] = useState<string | number>('');
    const [tempData, setTempData] = useState<any>(null);
    
    // 添加新记录
    const handleAdd = () => {
        const newRecord = {
            id: Date.now(),
            bankName: '',
            amount: 0,
            lendingDate: new Date(),
            endDate: null,
            paymentMethod: '',
            interest: 0.0035,
            oneTermAmount: 0,
            firstPayDate: null,
            editing: true
        };
        
        onChange([...data, newRecord]);
        setEditingKey(newRecord.id);
        setTempData({ ...newRecord });
    };
    
    // 开始编辑
    const handleEdit = (record: any) => {
        setEditingKey(record.id);
        setTempData({ ...record });
    };
    
    // 取消编辑
    const handleCancel = () => {
        setEditingKey('');
        setTempData(null);
    };
    
    // 保存编辑
    const handleSave = () => {
        if (!tempData) return;
        
        const updatedData = data.map(item => {
            if (item.id === editingKey) {
                return { ...tempData, editing: false };
            }
            return item;
        });
        
        onChange(updatedData);
        setEditingKey('');
        setTempData(null);
        Toast.success('保存成功');
    };
    
    // 删除记录
    const handleDelete = (id: string | number) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这条银行放款记录吗？',
            onOk: () => {
                const updatedData = data.filter(item => item.id !== id);
                onChange(updatedData);
                Toast.success('删除成功');
            }
        });
    };
    
    // 处理字段变化
    const handleFieldChange = (field: string, value: any) => {
        if (tempData) {
            setTempData({
                ...tempData,
                [field]: value
            });
        }
    };
    
    // 计算总金额
    const totalAmount = data.reduce((sum, item) => sum + (item.amount || 0), 0);
    
    // 列定义
    const columns = [
        {
            title: '银行名称',
            dataIndex: 'bankName',
            key: 'bankName',
            width: 150,
            render: (text: string, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <Input
                            value={tempData?.bankName}
                            onChange={value => handleFieldChange('bankName', value)}
                            placeholder="请输入银行名称"
                        />
                    );
                }
                return (
                    <Space>
                        <IconBranch />
                        <Text>{text || '未填写'}</Text>
                    </Space>
                );
            }
        },
        {
            title: '金额',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            render: (value: number, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <InputNumber
                            value={tempData?.amount}
                            onChange={value => handleFieldChange('amount', value)}
                            min={0}
                            step={1000}
                        />
                    );
                }
                return formatAmountZhCn(value);
            }
        },
        {
            title: '放款日期',
            dataIndex: 'lendingDate',
            key: 'lendingDate',
            width: 120,
            render: (value: Date, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <DatePicker
                            value={tempData?.lendingDate}
                            onChange={value => handleFieldChange('lendingDate', value)}
                            type="date"
                        />
                    );
                }
                return formatDate(value);
            }
        },
        {
            title: '到期日期',
            dataIndex: 'endDate',
            key: 'endDate',
            width: 120,
            render: (value: Date, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <DatePicker
                            value={tempData?.endDate}
                            onChange={value => handleFieldChange('endDate', value)}
                            type="date"
                        />
                    );
                }
                return value ? formatDate(value) : '-';
            }
        },
        {
            title: '还款方式',
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            width: 120,
            render: (text: string, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <Input
                            value={tempData?.paymentMethod}
                            onChange={value => handleFieldChange('paymentMethod', value)}
                            placeholder="如：等额本息"
                        />
                    );
                }
                return text || '-';
            }
        },
        {
            title: '年化利率',
            dataIndex: 'interest',
            key: 'interest',
            width: 100,
            render: (value: number, record: any) => {
                if (record.id === editingKey) {
                    return (
                        <InputNumber
                            value={tempData?.interest}
                            onChange={value => handleFieldChange('interest', value)}
                            step={0.0001}
                        />
                    );
                }
                return value ? `${(value * 100).toFixed(2)}%` : '-';
            }
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (record: any) => {
                if (record.id === editingKey) {
                    return (
                        <Space>
                            <Button
                                type="primary"
                                icon={<IconSave />}
                                onClick={handleSave}
                                size="small"
                            >
                                保存
                            </Button>
                            <Button
                                type="tertiary"
                                onClick={handleCancel}
                                size="small"
                            >
                                取消
                            </Button>
                        </Space>
                    );
                }
                
                return (
                    <Space>
                        <Button
                            type="tertiary"
                            icon={<IconEdit />}
                            onClick={() => handleEdit(record)}
                            size="small"
                        />
                        <Button
                            type="danger"
                            icon={<IconDelete />}
                            onClick={() => handleDelete(record.id)}
                            size="small"
                        />
                    </Space>
                );
            }
        }
    ];
    
    return (
        <div className="bank-lending-table">
            <div className="table-header">
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space>
                        <Button
                            type="primary"
                            icon={<IconPlus />}
                            onClick={handleAdd}
                            disabled={loading}
                        >
                            添加放款记录
                        </Button>
                        
                        <Tag color="blue" size="large">
                            总金额: {formatAmountZhCn(totalAmount)}
                        </Tag>
                        
                        <Tag color="green" size="large">
                            记录数: {data.length}
                        </Tag>
                    </Space>
                    
                    {editingKey && (
                        <Text type="secondary">
                            正在编辑，请保存或取消
                        </Text>
                    )}
                </Space>
            </div>
            
            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                pagination={false}
                scroll={{ y: 300 }}
                size="small"
            />
            
            {data.length === 0 && (
                <div className="empty-state">
                    <Text type="tertiary">
                        暂无银行放款记录，点击"添加放款记录"按钮添加
                    </Text>
                </div>
            )}
        </div>
    );
};

export default BankLendingTable;